import { AIModelChoices } from 'api';
import { useTranslation } from 'i18n';
import { type ComponentProps, useEffect } from 'react';
import { Box, CenteredLoader, Container, Grid, isVideo } from 'ui';
import {
  AiLooseAnalysisDataCard,
  HDPECompositionQualityReportDataCard,
  LoadingDetailsDataCard,
  MaterialSpecificationsDataCard,
  PETCompositionQualityReportDataCard,
  SiteDataCard,
  TargetBaleWeightsDataCard,
  TransactionHistoryDataCard,
  WeightSampleAnalysisDataCard,
} from '../../../data-cards';
import { ErrorPage } from '../../../error/components/ErrorPage/ErrorPage';
import { isHdpeAiModel, isPetBaledAiModel } from '../../../evidence/types';
import type { QualityReportTrackingSource } from '../../../types';
import { useQualityReportData } from '../../hooks/useQualityReportData/useQualityReportData';
import { useQualityReportMedia } from '../../hooks/useQualityReportMedia/useQualityReportMedia';
import { useSampleData } from '../../hooks/useSampleData/useSampleData';
import { SiteMaterialQualityReportFooter } from '../SiteMaterialQualityReportFooter/SiteMaterialQualityReportFooter';
import { SiteMaterialQualityReportHeader } from '../SiteMaterialQualityReportHeader/SiteMaterialQualityReportHeader';

const MINIMUM_ACCEPTED_BALES = 5;

export const SiteMaterialQualityReport = ({
  siteMaterialId,
  slotProps,
  placeholderSrc,
  handleTrackView,
  source,
}: {
  siteMaterialId: string;
  slotProps?: {
    header?: Partial<ComponentProps<typeof SiteMaterialQualityReportHeader>>;
  };
  placeholderSrc?: string;
  handleTrackView?: ({ material, materialId }: { material: string; materialId: number }) => void;
  source: QualityReportTrackingSource;
}) => {
  const { t } = useTranslation('features');
  const {
    data,
    error,
    sampleMaterial,
    isLoading: isLoadingData,
    hasEnoughSiteData,
    hasEnoughLoadingData,
    hasEnoughMaterialSpecificationData,
  } = useQualityReportData(siteMaterialId);

  const { manualSamples, hasSampleEvidence } = useSampleData(siteMaterialId);
  const {
    evidence,
    balesAccepted,
    isLoading: isLoadingEvidence,
  } = useQualityReportMedia(siteMaterialId, { pageSize: 5 });

  const isLoading = isLoadingData || isLoadingEvidence;

  useEffect(() => {
    if (!isLoading && !isLoadingEvidence && data) {
      handleTrackView?.({ material: data.material, materialId: data.id });
    }
    // We need to run this only once, but after the data is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100dvh',
        }}
      >
        <CenteredLoader />
      </Box>
    );
  }

  // Ensure marketing photos are prioritised
  // We're showing only 5 photos but can take from other categories if necessary
  const orderedPhotoEvidence = evidence.marketing.slice(0, 5);
  if (orderedPhotoEvidence.length < 5) {
    orderedPhotoEvidence.push(
      ...[
        ...evidence.site,
        ...evidence.accepted,
        ...evidence.specification,
        ...evidence.loading,
        ...evidence.ai_loose,
      ].slice(0, 5 - orderedPhotoEvidence.length)
    );
  }

  if (error || !data) {
    return <ErrorPage message={t('qualityReport.page.notAvailable')} href="/" />;
  }

  const orderedPhotoEvidencePreviews = orderedPhotoEvidence.map((evidenceItem) => {
    if (isVideo(evidenceItem.url)) {
      return evidenceItem.url;
    }
    return String(evidenceItem.preview || evidenceItem.url);
  });

  return (
    <Container
      sx={(theme) => ({
        backgroundColor: theme.palette.common.white,
        minHeight: `calc(100dvh - ${theme.constants.groundControlNavbarHeight}px)`,
        padding: 0,
      })}
    >
      <SiteMaterialQualityReportHeader
        originCountry={data.origin_country}
        material={data.material}
        {...slotProps?.header}
        grade={slotProps?.header?.grade || data.grade}
        photoEvidence={orderedPhotoEvidencePreviews}
        placeholderSrc={placeholderSrc}
        source={source}
      />
      <Grid
        container
        spacing={4}
        sx={{
          padding: 2,
        }}
      >
        {data.detected_ubc_content &&
        data.detected_ubc_content.target_bale_weights.length > 0 &&
        !isPetBaledAiModel(data.ai_model) &&
        balesAccepted >= MINIMUM_ACCEPTED_BALES ? (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <TargetBaleWeightsDataCard
              detectedUbcContent={data.detected_ubc_content}
              grade={data.grade}
              siteMaterialId={siteMaterialId}
            />
          </Grid>
        ) : null}

        {(data?.alu_loose_ai_composition || data?.pet_loose_ai_composition) && data?.ai_gravimetric_sample ? (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <AiLooseAnalysisDataCard
              isLoading={isLoading}
              composition={data?.alu_loose_ai_composition || data?.pet_loose_ai_composition || undefined}
              aiModel={data?.alu_loose_ai_composition ? AIModelChoices.alu_loose : AIModelChoices.pet_loose}
              hasMedia={evidence.ai_loose.length > 0}
              sampleDate={data?.ai_gravimetric_sample?.date}
              totalWeight={data?.ai_gravimetric_sample?.total_weight}
            />
          </Grid>
        ) : null}

        {isPetBaledAiModel(data.ai_model) && data.detected_pet_content ? (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <PETCompositionQualityReportDataCard
              isLoading={isLoading}
              balesAnalysed={Number(balesAccepted)}
              latestInspection={data.latest_inspection}
              composition={data.detected_pet_content.pet_composition}
              hasMedia={evidence.accepted.length > 0}
              modelName={data.ai_model}
            />
          </Grid>
        ) : null}

        {isHdpeAiModel(data.ai_model) ? (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <HDPECompositionQualityReportDataCard
              isLoading={isLoading}
              balesAnalysed={Number(balesAccepted)}
              latestInspection={data.latest_inspection}
              composition={data.detected_hdpe_content?.hdpe_composition ?? null}
              hasMedia={evidence.accepted.length > 0}
            />
          </Grid>
        ) : null}

        {manualSamples?.length > 0 && (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <WeightSampleAnalysisDataCard
              isLoading={isLoading}
              samples={manualSamples}
              sampleMaterial={sampleMaterial}
              hasMedia={hasSampleEvidence}
            />
          </Grid>
        )}

        {hasEnoughMaterialSpecificationData ? (
          <Grid size={12}>
            <MaterialSpecificationsDataCard
              isLoading={isLoading}
              storageType={data.specification?.storage_type}
              materialStrapping={data.specification?.material_strapping}
              height={data.specification?.height}
              width={data.specification?.width}
              length={data.specification?.length}
              weight={data.specification?.weight}
              visualAnalysis={data.specification?.visual_analysis}
              radiationAnalysis={data.specification?.radiation_analysis}
              ferrousAnalysis={data.specification?.ferrous_analysis}
              materialAnalysisNotes={data.specification?.material_analysis_notes}
              materialOrigin={data?.material_product?.material_origin?.type}
              materialProcess={data?.material_product?.creation_process}
              hasCapsAndLabels={data?.material_product?.has_caps_and_labels}
              isFoodGrade={data?.material_product?.is_food_grade}
              hasMedia={evidence.specification.length > 0}
            />
          </Grid>
        ) : null}

        {hasEnoughLoadingData ? (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <LoadingDetailsDataCard
              isLoading={isLoading}
              loadingTechnique={data.specification?.loading_technique}
              targetPayload={data.specification?.typical_container_weight}
              packedOn={data.specification?.packed_on}
              loadingNotes={data.specification?.loading_notes}
              hasMedia={evidence.loading.length > 0}
            />
          </Grid>
        ) : null}

        {hasEnoughSiteData ? (
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
          >
            <SiteDataCard
              isLoading={isLoading}
              storageConditions={data.specification?.storage_conditions}
              inputMaterialNotes={data.specification?.input_material_notes}
              machinesUsedNotes={data.specification?.machines_used_notes}
              storedOn={data.specification?.stored_on}
              productionVolume={data.specification?.production_volume}
              siteNotes={data.specification?.site_notes}
              hasMedia={evidence.site.length > 0}
            />
          </Grid>
        ) : null}
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <TransactionHistoryDataCard sellerId={data.trade_company_id} />
        </Grid>
      </Grid>
      <SiteMaterialQualityReportFooter siteMaterialId={siteMaterialId} />
    </Container>
  );
};
